import { z } from 'zod';
/**
 * The systems Bliks names records in. The CRM stays where it is; the billing
 * systems are the ones a customer can move between (LIC-24).
 */ export const ExternalSystemSchema = z.enum([
    'hubspot',
    'stripe',
    'frisbii',
    'moco',
    'docusign'
]);
/** The subset that collects money — what a `customer` ref can point into. */ export const BillingSystemSchema = z.enum([
    'stripe',
    'frisbii'
]);
/**
 * What the foreign record IS, in our words. `company` and `customer` hang off
 * an account, `purchase` and `position` off a licence.
 */ export const ExternalRefKindSchema = z.enum([
    'company',
    'customer',
    'purchase',
    'position',
    /** Der HubSpot-Deal eines Angebots (plan 048). */ 'deal',
    /** Das Angebot in Moco. */ 'offer',
    /** Der DocuSign-Umschlag. */ 'envelope',
    /** Die Rechnung in Moco zu einer unterschriebenen Fassung (plan 048, Schritt 14). */ 'invoice'
]);
/**
 * A foreign id as data, not as a column (LIC-24 §2). Which system currently
 * holds a customer is a value on the row, so two systems during a switch are
 * expressible — and "both columns filled, which one applies?" is not.
 */ export const ExternalRefSchema = z.object({
    system: ExternalSystemSchema,
    kind: ExternalRefKindSchema,
    id: z.string().min(1)
});
/** The id a record carries in one system, or `null` when it has none there. */ export function refOf(refs, system, kind) {
    return refs?.find((ref)=>ref.system === system && ref.kind === kind)?.id ?? null;
}
/**
 * The refs with one (system, kind) replaced or added. At most one ref per
 * (system, kind) is the rule every writer keeps — replacing is how a customer
 * moves systems, never by adding a second ref beside the first.
 */ export function withRef(refs, ref) {
    return [
        ...(refs ?? []).filter((r)=>!(r.system === ref.system && r.kind === ref.kind)),
        ref
    ];
}
export function withoutRef(refs, system, kind) {
    return (refs ?? []).filter((r)=>!(r.system === system && r.kind === kind));
}
/**
 * The one billing customer an account has, if any. One rather than a list:
 * an account with two live billing customers is exactly the "which one
 * applies" state the refs exist to make unwritable.
 */ export function billingCustomerOf(refs) {
    const ref = refs?.find((r)=>r.kind === 'customer' && BillingSystemSchema.safeParse(r.system).success);
    return ref ? {
        system: ref.system,
        id: ref.id
    } : null;
}

//# sourceMappingURL=external-ref.schema.js.map